// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/style-axis.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// style-axis.ts — the DOM half of the style axis (styles.ts is the DOM-free
// registry; keep it that way, the gates transpile it standalone).
//
// Three jobs:
//   1. STAMP  — `data-style` on <html>, beside `data-theme`. Same mechanism,
//      same pre-paint replay, same "the attribute is the source of truth
//      mid-toggle" rule. Every surface that stamps data-theme also stamps
//      this one, so a style reaches the Helm, both portals, the Bridge home
//      and (by inheritance — pi views render inside the host document, they
//      have no root of their own) the pi chat.
//   2. GROUND — the z-index -1 background layer, four grades. Blueprint's grade
//      is `gradient`, which is CSS-only and is precisely today's `body`
//      background, so the layer element does not exist in the shipped app.
//      Texture and canvas insert it; the canvas grade is capped at ONE,
//      paused on a hidden tab, and refused outright under ANY of the three
//      reduce lanes — reduced motion, reduced transparency, or the app's own
//      `[data-reduce="1"]` switch. The layer sits at z-index -1 (not 0) so it
//      is structurally under in-flow content, and its canvas descendant is
//      trapped in that context — no per-style z-index workaround is needed.
//   3. SEAT   — resolve the dark-launch seat and SAY SO at boot. The
//      Phase-1 scar here is a feature wired to a stub that boot-logged
//      nothing; this module prints the resolved identity of the axis on
//      every boot, so "is the picker live?" is answered by the log, not by
//      reading source.

import {
  BLUEPRINT,
  DEFAULT_STYLE_ID,
  MAX_BACKGROUND_CANVASES,
  STYLE_SEAT_PARAM,
  STYLE_SEAT_SESSION_KEY,
  normalizeStyleId,
  offeredStyles,
  resolveStyle,
  resolveStyleSeat,
  type AppStyle,
  type BackgroundGrade,
  type StyleSeat,
} from './styles.js';

/** Pre-paint cache key — index.html replays it before the stylesheet loads,
 * exactly as `bridge-theme` does for the mode. */
export const STYLE_PREPAINT_KEY = 'bridge-style';

/** The id of the inserted ground layer, and of the one canvas inside it. */
export const BG_LAYER_ID = 'style-bg';
export const BG_CANVAS_ID = 'style-bg-canvas';
/** THE SURFACE'S HORIZON SEAT. A ground with a horizon (Synthwave's plane;
 * any later style with one) lays it at `--sw-horizon`, which the stylesheet
 * declares as `var(--bgl-horizon, 38%)` — so a SURFACE writes this property
 * (a `px` chrome edge or a `%` of the viewport) on <html> to say where its
 * open ground begins, and writes nothing to keep the style's default. It is
 * a surface seat, not a style token: no style declares it and
 * BACKGROUND_VAR_NAMES does not list it. Owner ruling 2026-09-08 ("the
 * background of the bridge-console should be visible in the space behind the
 * jump back in section"): measured on the Bridge home, the band sits at
 * ~12-31% of the viewport and the opaque releases grid fills everything below
 * ~37%, so a 38% horizon left Synthwave's whole field under the table. The
 * Helm writes no seat and keeps 38%. */
export const GROUND_HORIZON_SEAT = '--bgl-horizon';

// ---------------------------------------------------------------------
// 1. The attribute
// ---------------------------------------------------------------------

/** The style as PAINTED — `data-style` on <html>. Unlike the mode (where
 * "dark" is the absence of an attribute), the style attribute is ALWAYS
 * stamped: a style is a positive statement about materials, and a missing
 * attribute must fall through to the bare `:root` Blueprint block rather
 * than to nothing. */
export function currentStyle(): string {
  if (typeof document === 'undefined') return DEFAULT_STYLE_ID;
  return normalizeStyleId(document.documentElement.getAttribute('data-style') ?? undefined);
}

/**
 * THE apply path for the style axis — the twin of applyAppTheme.
 *
 * Stamps the attribute, mirrors it into the pre-paint cache, and re-grounds
 * the background layer. Returns the resolved style so callers can log it.
 */
export function applyAppStyle(id: string | undefined): AppStyle {
  const style = resolveStyle(id);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-style', style.id);
    try {
      localStorage.setItem(STYLE_PREPAINT_KEY, style.id);
    } catch {
      // storage unavailable (private mode) — pre-paint just defaults Blueprint
    }
  }
  applyStyleBackground(style);
  return style;
}

// ---------------------------------------------------------------------
// 2. The ground
// ---------------------------------------------------------------------

/** Viewer-preference gates. Both are hard refusals, not dimmers: a viewer
 * who asked for reduced transparency did not ask for a quieter animation. */
export function motionAllowed(): boolean {
  if (typeof matchMedia !== 'function') return true;
  return !matchMedia('(prefers-reduced-motion: reduce)').matches;
}
export function transparencyAllowed(): boolean {
  if (typeof matchMedia !== 'function') return true;
  return !matchMedia('(prefers-reduced-transparency: reduce)').matches;
}
/** Lane 3 — the app's OWN reduce-effects switch, as reflected on <html> by
 * effects-seat.ts (`data-reduce="1"`). The two OS lanes above ride matchMedia;
 * this one rides the attribute, so effectiveGrade() honours all three ways a
 * viewer can ask for less. Reading the ATTRIBUTE (not effects-seat's
 * localStorage seat) keeps this module DOM-only with no import cycle back
 * through the effects module, and it is the exact state the CSS reduce arms
 * match — so JS and CSS cannot disagree about whether this lane is on. */
export function reduceEffectsActive(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-reduce') === '1';
}

/**
 * The grade this viewer actually gets. A refused grade degrades one rung
 * down the ladder rather than to nothing — `canvas` under any reduce lane is
 * still a designed background, just a still one.
 *
 * THREE reduce lanes, OR-ed: prefers-reduced-motion, prefers-reduced-
 * transparency, and the app's own `[data-reduce="1"]` switch. The app switch
 * once did NOT reach here (canvas styles left the rAF running behind an
 * invisible-or-hidden canvas — GLASS did not even hide it), which is the
 * substrate gap this seat closes: an animated ground is TORN DOWN under the
 * app switch, not merely hidden, because applyStyleBackground tears down the
 * old ground and never builds the degraded one.
 */
export function effectiveGrade(
  want: BackgroundGrade,
  env: { motion?: boolean; transparency?: boolean; reduce?: boolean } = {},
): BackgroundGrade {
  const motion = env.motion ?? motionAllowed();
  const transparency = env.transparency ?? transparencyAllowed();
  const reduce = env.reduce ?? reduceEffectsActive();
  // The app's own switch is a hard stop for the ANIMATED ground exactly like
  // the two OS lanes: a canvas degrades to the still gradient, so no element is
  // built and no rAF is scheduled. Texture is static (no rAF), so it stays
  // governed by transparency alone — the same treatment reduced-motion gets.
  if (want === 'canvas' && (!motion || !transparency || reduce)) return 'gradient';
  if (want === 'texture' && !transparency) return 'gradient';
  return want;
}

/** A style's animated-canvas painter. Registered by the style's own PR; the
 * substrate ships none, which is why `canvasPainters` is empty here and the
 * gate's "more than one canvas" arm is a live constraint rather than a
 * hypothetical. */
export type CanvasPainter = (ctx: CanvasRenderingContext2D, t: number) => void;
const canvasPainters = new Map<string, CanvasPainter>();

/** Register the painter for a style's animated background. Refuses a second
 * painter for the same style id (a silent overwrite is how two backgrounds
 * end up fighting over one canvas). */
export function registerCanvasPainter(styleId: string, paint: CanvasPainter): void {
  if (canvasPainters.has(styleId)) {
    throw new Error(`style-axis: a canvas painter is already registered for style '${styleId}'`);
  }
  canvasPainters.set(styleId, paint);
}

/** Test seam — the gate drives the machinery with a synthetic style. */
export function clearCanvasPainters(): void {
  canvasPainters.clear();
}

let rafHandle: number | null = null;
let visibilityBound = false;
let liveCanvasStyleId: string | null = null;
/** WHAT IS LIVE RIGHT NOW — module state, not a closure capture.
 *
 * The visibilitychange listener is bound ONCE and lives forever, but the
 * canvas does not: applyStyleBackground tears the ground down and builds a
 * NEW element on every call, and it is called on every applyAppTheme, not
 * only on a style switch. A listener holding the first canvas in its closure
 * would, after any repaint, resume painting into a DETACHED element on the
 * next tab focus while the live one stayed frozen — a background that stops
 * moving with nothing in the console to say why. Found while wiring
 * STARSHIP's light-slot teardown (PR-6), reachable by GLASS too; the fix is
 * that the listener reads what is live instead of what was. */
let liveCanvas: HTMLCanvasElement | null = null;
let livePainter: CanvasPainter | null = null;

/** How many background canvases are in the document RIGHT NOW. The single
 * source for the cap — perf-budget.mjs drives the machinery and reads this,
 * so the number the gate reports is the number the app would render. */
export function backgroundCanvasCount(): number {
  if (typeof document === 'undefined') return 0;
  return document.querySelectorAll(`canvas#${BG_CANVAS_ID}`).length;
}

/** Whether the rAF loop is running. Read by the gate's pause-on-hidden arm
 * and by the smoke — an unpausable loop must be provable, not asserted. */
export function backgroundTicking(): boolean {
  return rafHandle !== null;
}

/** Whether the pause-on-hidden wiring is armed. A canvas background without
 * this is a tab that burns a core in the background forever; the gate
 * refuses one. */
export function pauseOnHiddenArmed(): boolean {
  return visibilityBound;
}

function stopTicking(): void {
  if (rafHandle !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafHandle);
  rafHandle = null;
}

function startTicking(canvas: HTMLCanvasElement, paint: CanvasPainter): void {
  if (rafHandle !== null) return;
  if (typeof requestAnimationFrame !== 'function') return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const step = (t: number): void => {
    // Belt AND braces: the visibility listener stops the loop, and the loop
    // itself refuses to schedule another frame while hidden. A browser that
    // fires no visibilitychange (or a bfcache restore that skips it) must
    // still not leave a hidden tab painting.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      rafHandle = null;
      return;
    }
    paint(ctx, t);
    rafHandle = requestAnimationFrame(step);
  };
  rafHandle = requestAnimationFrame(step);
}

function bindVisibility(): void {
  if (visibilityBound || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') stopTicking();
    else if (liveCanvasStyleId && liveCanvas && livePainter) startTicking(liveCanvas, livePainter);
  });
  visibilityBound = true;
}

/** The canvas the loop would resume into on the next tab focus. Read by the
 * gate/smoke arm that proves the listener follows a rebuilt ground rather
 * than the first one it ever saw. */
export function liveBackgroundCanvas(): HTMLCanvasElement | null {
  return liveCanvas;
}

/** Tear the ground down completely: canvas, layer element, rAF. Called on
 * every apply BEFORE the new grade is built, which is what makes the
 * one-canvas cap hold across a style switch. */
export function teardownStyleBackground(): void {
  stopTicking();
  liveCanvasStyleId = null;
  liveCanvas = null;
  livePainter = null;
  if (typeof document === 'undefined') return;
  for (const el of document.querySelectorAll(`#${BG_LAYER_ID}`)) el.remove();
}

/**
 * Ground the document for a style.
 *
 *   flat / gradient — CSS only. The `--bgl-*` tokens in the style's block
 *                     already describe the ground; nothing is inserted, and
 *                     `body`'s background reads them. This is the shipped
 *                     path (Blueprint is `gradient`).
 *   texture         — inserts the ONE layer element at z-0.
 *   canvas          — inserts the layer element and the ONE canvas, binds
 *                     pause-on-hidden, and starts the rAF loop.
 *
 * Returns the grade actually applied (which may be a degraded one).
 */
export function applyStyleBackground(
  style: AppStyle,
  env: { motion?: boolean; transparency?: boolean } = {},
): BackgroundGrade {
  const grade = effectiveGrade(style.background, env);
  teardownStyleBackground();
  if (typeof document === 'undefined' || !document.body) return grade;
  if (grade === 'flat' || grade === 'gradient') return grade;

  const layer = document.createElement('div');
  layer.id = BG_LAYER_ID;
  layer.className = `style-bg style-bg-${grade}`;
  layer.setAttribute('aria-hidden', 'true');

  if (grade === 'canvas') {
    const painter = canvasPainters.get(style.id);
    if (!painter) {
      // A style that declares a canvas ground but ships no painter would
      // render an empty black rectangle over the whole app. Degrade instead,
      // loudly — this is a programming error in a style PR, not a viewer
      // condition, so it is not silent.
      console.warn(`style-axis: style '${style.id}' asks for a canvas ground but registered no painter — falling back to gradient`);
      return 'gradient';
    }
    if (backgroundCanvasCount() >= MAX_BACKGROUND_CANVASES) {
      // Unreachable through this function (teardown ran above); reachable if
      // some other code inserted one. Refuse rather than add a second.
      console.warn('style-axis: a background canvas already exists — refusing a second');
      return 'gradient';
    }
    const canvas = document.createElement('canvas');
    canvas.id = BG_CANVAS_ID;
    canvas.className = 'style-bg-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    layer.appendChild(canvas);
    document.body.prepend(layer);
    liveCanvasStyleId = style.id;
    liveCanvas = canvas;
    livePainter = painter;
    bindVisibility();
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return grade;
    startTicking(canvas, painter);
    return grade;
  }

  document.body.prepend(layer);
  return grade;
}

// ---------------------------------------------------------------------
// 3. The seat
// ---------------------------------------------------------------------

/** The per-session override lane: `?style-picker=on|off` sticks in
 * sessionStorage for the tab, so the owner checkpoint ("flipped for your
 * session only") needs no deploy and cannot leak to anyone else. */
export function sessionStyleSeatOverride(): boolean | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const q = new URLSearchParams(window.location.search).get(STYLE_SEAT_PARAM);
    if (q === 'on' || q === '1') {
      sessionStorage.setItem(STYLE_SEAT_SESSION_KEY, 'on');
      return true;
    }
    if (q === 'off' || q === '0') {
      sessionStorage.setItem(STYLE_SEAT_SESSION_KEY, 'off');
      return false;
    }
    const stored = sessionStorage.getItem(STYLE_SEAT_SESSION_KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    // private windows / no sessionStorage — the other two lanes still decide
  }
  return undefined;
}

let seat: StyleSeat = { enabled: false, source: 'default' };

/** The resolved seat. Every picker asks this; nothing reads the constant. */
export function styleSeat(): StyleSeat {
  return seat;
}

/** The styles a picker may offer right now. */
export function pickableStyles(): AppStyle[] {
  return offeredStyles(seat.enabled);
}

/**
 * Paint a style pick THROUGH THE SEAT.
 *
 * This, not applyAppStyle, is what every caller outside this module uses.
 * The seat gates the PAINT and not merely the dropdown: if it only hid the
 * picker, someone who had chosen a style before the seat was switched back
 * off would be stranded on a style they could no longer change. A pick the
 * seat does not offer paints Blueprint.
 */
export function paintStyle(id: string | undefined): AppStyle {
  const allowed = new Set(pickableStyles().map((s) => s.id));
  const wanted = normalizeStyleId(id);
  return applyAppStyle(allowed.has(wanted) ? wanted : DEFAULT_STYLE_ID);
}

/**
 * Resolve the picker seat, and ONLY the seat: no paint, no ground, no log.
 * The same three lanes bootStyleAxis() resolves (the per-tab
 * `?style-picker=` override, then the Bridge's `prefs.stylePicker`, then
 * STYLE_PICKER_SEAT_DEFAULT). A surface that owns its own paint path — the
 * docs portals, which paint through portal/theme.ts — calls this so its
 * picker offers what the Helm's does; before #3242 no portal called either,
 * so `seat` stayed at its module initial and the portals offered Blueprint
 * alone with no Style dropdown at all.
 */
export function bootStyleSeat(opts: { serverPref?: boolean } = {}): StyleSeat {
  seat = resolveStyleSeat({
    sessionOverride: sessionStyleSeatOverride(),
    serverPref: opts.serverPref,
  });
  return seat;
}

/**
 * Boot the axis. Called once, early, with the persisted style pick and the
 * Bridge's own `prefs.stylePicker` seat value (undefined off-console or on a
 * Bridge that predates the seat).
 *
 * Prints ONE line naming the resolved identity of every seam — which style
 * is painted, whether the picker is live and WHICH LANE decided that, the
 * background grade actually applied after the viewer-preference gates, and
 * how many styles are registered. That line is the answer to "is this wired
 * to anything?", which is the question Phase 1 could not answer for 98
 * merged tasks.
 */
export function bootStyleAxis(opts: { styleId?: string; serverPref?: boolean } = {}): {
  style: AppStyle;
  seat: StyleSeat;
  grade: BackgroundGrade;
} {
  bootStyleSeat({ serverPref: opts.serverPref });
  // With the seat OFF the picker offers Blueprint alone — so a stored pick
  // for a style the viewer may not select must not paint either. The seat
  // gates the PAINT, not just the dropdown; otherwise flipping the seat off
  // would leave people stranded on a style they can no longer change.
  const wanted = normalizeStyleId(opts.styleId);
  const style = paintStyle(opts.styleId);
  const painted = style.id;
  const grade = effectiveGrade(style.background);
  console.log(
    `style axis: style=${style.id}${painted === wanted ? '' : ` (requested ${wanted}, not offered)`}` +
      ` · picker seat ${seat.enabled ? 'ON' : 'OFF'} (${seat.source})` +
      ` · offered ${pickableStyles().length}/${offeredStyles(true).length} styles` +
      ` · ground ${style.background}${grade === style.background ? '' : ` -> ${grade}`}` +
      ` · canvases ${backgroundCanvasCount()}/${MAX_BACKGROUND_CANVASES}`,
  );
  return { style, seat, grade };
}

/** Test seam: reset the module between gate arms. */
export function resetStyleAxis(): void {
  teardownStyleBackground();
  clearCanvasPainters();
  visibilityBound = false;
  seat = { enabled: false, source: 'default' };
}

export { BLUEPRINT };
